import { type IContent } from "@kunduz/data-access-cms-api";
import React, { PropsWithChildren, ReactElement } from "react";
import styles from "./planDetailV2.module.scss";
import { Text } from "@kunduz/ui-atoms";
import {
  Accordion,
  PlanDetailInstallmentTable,
  PlanDetailBulletList,
  PlanDetailPlainText,
  PlanDetailAccordionCarousel,
  PlanDetailAvatarGroup,
  gray,
  PlanDetailDescriptionPlainText,
  PlanDetailLogoList,
} from "@kunduz/ui-components";
import classNames from "classnames";
import { Divider } from "@kunduz/ui";
import { trackEventNext } from "@kunduz/utility-analytics";
import { AccordionTypes } from "./accordionTypesEnum";

export const PlanDetailAccordion = (props: Props): ReactElement => {
  /**
   * returns correct sub component for acoordion content
   */
  function AccordionSubComponentSwitcher(props: {
    data: IContent.PlanDetailV2Option<IContent.PlanDetailV2AccordionData>["data"]["items"][0]["items"][0];
  }) {
    switch (props.data.type) {
      case AccordionTypes.AccordionInstallmentGroupItem:
        return <PlanDetailInstallmentTable data={props.data.data} />;
      case AccordionTypes.AccordionBulletGroupItem:
        return <PlanDetailBulletList data={props.data.data} />;
      case AccordionTypes.AccordionPlainTextItem:
        return <PlanDetailPlainText data={props.data.data} />;
      case AccordionTypes.AccordionDescriptionPlainTextItem:
        return <PlanDetailDescriptionPlainText data={props.data.data} />;
      case "carouseloptioncomponent":
        return <PlanDetailAccordionCarousel data={props.data.data} />;
      case AccordionTypes.AccordionImageGroupItem:
        return <PlanDetailAvatarGroup data={props.data.data} />;
      case AccordionTypes.LogoListGroupComponent:
        return <PlanDetailLogoList data={props.data.data} />;

      default:
        return <></>;
    }
  }

  /**
   * accordion data has both legacy description and items
   * this code sanitizes it
   */
  function prepareAccordionChildren(
    accordionItemDesc?: string,
    items?: IContent.PlanDetailV2Option<IContent.PlanDetailV2AccordionData>["data"]["items"][0]["items"],
  ) {
    const legacy: { type: AccordionTypes; data: { text: string } }[] = [];

    if (accordionItemDesc) {
      legacy.push({
        type: AccordionTypes.AccordionDescriptionPlainTextItem,
        data: {
          text: accordionItemDesc,
        },
      });
    }

    const computed =
      [...legacy, ...(items || [])]?.map((subitem, subkey) => (
        <AccordionSubComponentSwitcher
          data={subitem}
          key={`subkey_${subkey}`}
        />
      )) || [];

    if (!computed || computed.length == 0) {
      return [];
    }

    return computed
      .reduce(
        (arr, b, index) =>
          [
            ...arr,
            b,
            <Divider
              top={0}
              bottom={0}
              color={gray[200]}
              key={`divider_${index}`}
            />,
          ] as any,
        [],
      )
      .slice(0, -1);
  }

  function PlanDetailAccordionContentWrapper(
    props: PropsWithChildren<{
      bgColor?: string;
      hasPadding?: boolean;
    }>,
  ) {
    return (
      <div
        className={classNames(styles.planDetailAccordionContent, {
          [styles["hasPadding"]]: props.hasPadding,
        })}
        style={{ backgroundColor: props.bgColor }}
      >
        {props.children}
      </div>
    );
  }

  function handleCollapseChanged(title: string) {
    trackEventNext({ event: "Accordion Item Clicked", title });
  }

  return (
    <div
      className={classNames(styles["singleAccordion"], {
        [styles.noTitle]: !props.accordionData.data.title,
      })}
    >
      {props.accordionData.data.title && (
        <Text
          variant="displayxs"
          weight="semibold"
          style={{ color: gray[900] }}
        >
          {props.accordionData.data.title}
        </Text>
      )}
      <Accordion
        accordionItems={props.accordionData.data.items?.map(
          (accordionItem) => ({
            title: accordionItem.title,
            iconUrl: accordionItem.icon,
            content: accordionItem.description,
            children: (
              <PlanDetailAccordionContentWrapper
                bgColor={accordionItem.bg_color}
                hasPadding={props.accordionData.data.has_padding}
              >
                {prepareAccordionChildren(
                  accordionItem.description,
                  accordionItem.items,
                )}
              </PlanDetailAccordionContentWrapper>
            ),
            onCollapseChanged: () => handleCollapseChanged(accordionItem.title),
          }),
        )}
      />
    </div>
  );
};

interface Props {
  accordionData: IContent.PlanDetailV2Option<IContent.PlanDetailV2AccordionData>;
}
